import { IkonG } from "../Ikon";
import { STEG } from "../Flode";
import { usePausUtanfor } from "../../lib/syns";

// Animerad bana ovanför stegkorten: en signal går från inkorgen genom avläsning, kontroller och
// kontering till attest. Kontering har en inlärningsslinga tillbaka från attesten, och vid
// överlämningen grenar en röd faktura av till handläggning. Bara stegnamn som redan finns i STEG.
// All rörelse är CSS (en cykel, procent-keyframes) och styrs i styles.css under "Flödesbana".
// Grundstilen är sluttillståndet, så reducerad rörelse visar en komplett, stilla bild.

type Layout = {
  id: string;
  vb: string;
  nod: (i: number) => [number, number];
  bana: string;
  slinga: string;
  rod: string;
  rodX: [number, number];
  etikett: (i: number, x: number, y: number) => { n: [number, number]; t: [number, number]; anchor: "middle" | "start" };
  grad: [number, number, number, number];
  banor?: boolean;
};

const H: Layout = {
  id: "fbh",
  vb: "0 0 1200 290",
  nod: (i) => [100 + i * 200, 150],
  bana: "M100 150 H1100",
  slinga: "M1100 110 C1100 52 700 52 700 110",
  rod: "M926 166 Q956 198 990 198",
  rodX: [1002, 198],
  grad: [100, 0, 1100, 0],
  etikett: (_i, x) => ({ n: [x, 230], t: [x, 256], anchor: "middle" }),
  banor: true,
};

const V: Layout = {
  id: "fbv",
  vb: "0 0 340 600",
  nod: (i) => [96, 56 + i * 96],
  bana: "M96 56 V536",
  slinga: "M58 536 C8 536 8 344 58 344",
  rod: "M122 452 Q150 486 194 486",
  rodX: [206, 486],
  grad: [0, 56, 0, 536],
  etikett: (_i, _x, y) => ({ n: [150, y - 6], t: [150, y + 18], anchor: "start" }),
};

function Svg({ L, className }: { L: Layout; className: string }) {
  const [x3, y3] = L.nod(2);
  return (
    <svg className={"fb-svg " + className} viewBox={L.vb} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={L.id + "-g"} gradientUnits="userSpaceOnUse" x1={L.grad[0]} y1={L.grad[1]} x2={L.grad[2]} y2={L.grad[3]}>
          <stop offset="0" stopColor="#5DD3DE" />
          <stop offset="1" stopColor="#93A6F5" />
        </linearGradient>
        <mask id={L.id + "-m"} maskUnits="userSpaceOnUse" x={x3 - 60} y={y3 - 60} width="120" height="120">
          <circle className="fb-tickmask" cx={x3} cy={y3} r="41" pathLength={1} transform={`rotate(-90 ${x3} ${y3})`} />
        </mask>
      </defs>

      {L.banor ? (
        <g className="fb-lanes">
          <text x="70" y="30">AGENTEN</text>
          <line x1="150" y1="26" x2="980" y2="26" className="fb-lane-a" stroke={`url(#${L.id}-g)`} />
          <text x="1100" y="30" textAnchor="middle" className="pers">PERSONALEN</text>
        </g>
      ) : null}

      {/* Banan: streckad grund, ifylld del som växer med signalen. */}
      <path className="fb-track" d={L.bana} />
      <path className="fb-prog" d={L.bana} pathLength={1} stroke={`url(#${L.id}-g)`} />

      {/* Inlärningsslingan från attest tillbaka till kontering. */}
      <path className="fb-loop" d={L.slinga} />
      <path className="fb-loop-komet" d={L.slinga} pathLength={1} />

      {/* Röd gren vid överlämningen. */}
      <path className="fb-rod" d={L.rod} />
      <path className="fb-rod-dot" d={L.rod} pathLength={1} />
      <g className="fb-rodx" transform={`translate(${L.rodX[0]} ${L.rodX[1]})`}>
        <circle r="10" />
        <path d="M-3.5 -3.5 L3.5 3.5 M3.5 -3.5 L-3.5 3.5" />
      </g>

      {/* Signalen: ett streck med längd noll och runda ändar blir en prick som flyttas med dashoffset. Ligger under noderna,
      så att den syns mellan stegen och lyser bakom noden när den stannar. */}
      <path className="fb-dot-halo" d={L.bana} pathLength={1} />
      <path className="fb-dot" d={L.bana} pathLength={1} />

      {STEG.map(([namn, , ikon], i) => {
        const [x, y] = L.nod(i);
        const e = L.etikett(i, x, y);
        return (
          <g key={namn}>
            <g className={`fb-nod n${i + 1}` + (i === 5 ? " human" : "")} transform={`translate(${x} ${y})`}>
              <circle className="fb-halo" r="44" />
              <circle className="fb-disk" r="30" />
              <circle className="fb-ring" r="30" />
              {i === 0 ? <g className="fb-drop"><rect x="-6" y="-8" width="12" height="15" rx="2" /><path d="M-3 -3.5h6M-3 0h6M-3 3.5h4" /></g> : null}
              {i === 1 ? (
                <g className="fb-scanwrap">
                  <clipPath id={L.id + "-c2"}><circle r="29" /></clipPath>
                  <g clipPath={`url(#${L.id}-c2)`}><rect className="fb-scan" x="-30" y="-1" width="60" height="2" /></g>
                </g>
              ) : null}
              {i === 2 ? (
                <>
                  <circle className="fb-ticks dim" r="41" pathLength={40} />
                  <circle className="fb-ticks lit" r="41" pathLength={40} mask={`url(#${L.id}-m)`} transform={`translate(${-x} ${-y})`} cx={x} cy={y} />
                </>
              ) : null}
              {i === 3 ? (
                <g className="fb-orbit">
                  <circle r="41" className="fb-orbit-bana" />
                  <circle cx="41" cy="0" r="3" /><circle cx="-41" cy="0" r="2.2" />
                </g>
              ) : null}
              <g className="fb-ikon" transform="translate(-11 -11) scale(0.9167)"><IkonG namn={ikon} /></g>
              {i === 5 ? <g transform="translate(22 -22)"><g className="fb-stamp"><circle r="9" /><path d="M-4 0.5 L-1.2 3.3 L4.2 -2.6" pathLength={1} /></g></g> : null}
            </g>
            <text className="fb-n" x={e.n[0]} y={e.n[1]} textAnchor={e.anchor}>{String(i + 1).padStart(2, "0")}</text>
            <text className={"fb-t" + (i === 5 ? " pers" : "")} x={e.t[0]} y={e.t[1]} textAnchor={e.anchor}>{namn}</text>
          </g>
        );
      })}

    </svg>
  );
}

export function FlodeBana() {
  const ref = usePausUtanfor<HTMLDivElement>();
  return (
    <div ref={ref} className="fbana">
      <Svg L={H} className="h" />
      <Svg L={V} className="v" />
    </div>
  );
}
